import ReceiptScanner from "./ReceiptScanner";
import { ScanLine } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Repeat, ArrowLeftRight } from "lucide-react";
import CurrencyPicker from "@/components/mm/CurrencyPicker";
import { useData } from "@/context/DataContext";
import { useFxRates } from "@/hooks/useFxRates";
import { money, today, curSymbol } from "@/lib/money";
import { t } from "@/lib/i18n";

function Converter({ fx, from, to, setFrom, setTo, home, amt }) {
  const r = fx.rate(from, to), rh = fx.rate(from, home);
  const fresh = fx.at && Date.now() - fx.at < 36e5;
  return (
    <div className="mt-3 rounded-[var(--ri)] border border-[var(--bd)] bg-[var(--bg)] p-3" data-testid="currency-converter-panel">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
        <CurrencyPicker value={from} onChange={setFrom} codes={fx.codes} testId="converter-from-picker" className="!bg-[var(--card)] !py-2 text-sm" />
        <button type="button" className="grid h-9 w-9 place-items-center rounded-full border border-[var(--bd)] bg-[var(--card)] transition-transform duration-300 hover:rotate-180" onClick={() => { setFrom(to); setTo(from); }} aria-label="Swap" data-testid="converter-swap-button"><ArrowLeftRight className="h-4 w-4" /></button>
        <CurrencyPicker value={to} onChange={setTo} codes={fx.codes} testId="converter-to-picker" className="!bg-[var(--card)] !py-2 text-sm" />
      </div>
      <div className="mt-2 text-xs text-[var(--mu)]" data-testid="converter-rate-line">
        {r > 0 ? (
          <>
            {amt > 0 && <b className="num mr-1 text-sm text-[var(--tx)]" data-testid="converter-result">{money(amt, from)} = {money(amt * r, to)}</b>}
            <span>· 1 {from} = {r.toFixed(4)} {to} ({fresh ? t("live rate") : t("last saved rate")})</span>
            {to !== home && amt > 0 && rh > 0 && <div className="mt-1">Saved to your records as <b className="num">{money(amt * rh, home)}</b> ({home} is your main currency)</div>}
          </>
        ) : t("Getting live exchange rates…")}
      </div>
    </div>
  );
}

function CatGrid({ cats, sel, setSel }) {
  return (
    <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 lg:grid-cols-8" data-testid="add-expense-category-grid">
      {cats.map((c) => (
        <button type="button" key={c.id} onClick={() => setSel(c.id)} className={`mm-cat ${sel === c.id ? "on" : ""}`} style={{ "--c": c.c }} data-testid={`category-option-${c.id.replace(/\s+/g, "-").toLowerCase()}`}>
          <span className="e">{c.e}</span><span className="line-clamp-2">{t(c.n)}</span>
        </button>
      ))}
    </div>
  );
}

export default function AddExpense({ onEditCats, onRecurring, initialScan = false }) {
  const { db, create, setAnchor, saveSettings, saving } = useData();
  const prefs = db.settings.prefs;
  const [scanOpen, setScanOpen] = useState(initialScan);
  const home = prefs.currency;
  const [amt, setAmt] = useState("");
  const [conv, setConv] = useState(false);
  const [from, setFromS] = useState(prefs.fxFrom && prefs.fxFrom !== home ? prefs.fxFrom : home === "USD" ? "BDT" : "USD");
  const [to, setTo] = useState(home);
  const [sel, setSel] = useState(null);
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [err, setErr] = useState("");
  const fx = useFxRates();
  const cats = db.settings.categories;

  useEffect(() => { setTo(home); }, [home]);
  useEffect(() => {
    const f = (e) => { setConv(false); setAmt(String(e.detail)); };
    window.addEventListener("mm-amount", f);
    return () => window.removeEventListener("mm-amount", f);
  }, []);

  const setFrom = (c) => { setFromS(c); if (c !== prefs.fxFrom) saveSettings({ prefs: { ...prefs, fxFrom: c } }).catch(() => {}); };

  const warnBudget = (cat, d, a) => {
    const custom = prefs.budgetStart && prefs.budgetEnd;
    if (custom && (d < prefs.budgetStart || d > prefs.budgetEnd)) return;
    const overall = prefs.budgetMode === "overall";
    const b = overall ? Number(prefs.overallBudget || 0) : db.settings.budgets[cat];
    if (!b) return;
    const spent = db.expenses.filter((x) => (overall || x.cat === cat) && (custom ? x.date >= prefs.budgetStart && x.date <= prefs.budgetEnd : x.date.startsWith(d.slice(0, 7)))).reduce((sum, x) => sum + x.amt, 0) + a;
    const label = overall ? "Overall budget" : cats.find((x) => x.id === cat)?.n || cat;
    if (spent > b) toast.error(`${label} is over budget: ${money(spent)} of ${money(b)}`);
    else if (spent >= b * .8) toast.warning(`${label} has used ${Math.round(spent / b * 100)}% of ${money(b)}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    let a = parseFloat(amt);
    let extra = "";
    if (!(a > 0)) return setErr(t("Enter an amount first."));
    if (conv && from !== home) {
      const r = fx.rate(from, home);
      if (!(r > 0)) return setErr("Waiting for the exchange rate. Check your internet and try again.");
      extra = `${money(a, from)} @ 1 ${from} = ${r.toFixed(4)} ${home}`;
      a = Math.round(a * r * 100) / 100;
      if (!(a > 0)) return setErr("Converted amount is too small.");
    }
    if (!sel) return setErr(t("Please choose a category."));
    setErr("");
    const d = date || today();
    try {
      await create("expenses", { amt: a, cat: sel, note: [note.trim(), extra].filter(Boolean).join(" · ").slice(0, 300), date: d });
      warnBudget(sel, d, a);
      setAnchor(new Date(d + "T00:00"));
      setAmt(""); setNote(""); setSel(null); setConv(false); setDate(today());
    } catch (x) { /* toast shown */ }
  };

  return (
    <section className="mm-card mm-rise" data-testid="add-expense-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="mm-h2 text-lg">{t("Add spending")}</h2>
        <div className="flex min-w-0 gap-1.5" aria-label="Spending tools">
          <button type="button" className="mm-btn ghost sm" onClick={() => setScanOpen(!scanOpen)} aria-label="Scan receipt" title="Scan receipt" aria-expanded={scanOpen} aria-controls="spending-receipt-scanner" data-testid="add-expense-scan-button"><ScanLine className="h-3.5 w-3.5" />{t("Scan")}</button>
          <button type="button" className="mm-btn ghost sm" onClick={onRecurring} data-testid="add-expense-recurring-button"><Repeat className="h-3.5 w-3.5" />{t("Recurring")}</button>
          <button type="button" className="mm-btn ghost sm" onClick={onEditCats} data-testid="add-expense-edit-categories-button"><Pencil className="h-3.5 w-3.5" />{t("Edit")}</button>
        </div>
      </div>
      {scanOpen && <div id="spending-receipt-scanner" role="region" aria-label="Receipt scanner"><ReceiptScanner /></div>}
      <form onSubmit={submit}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <span className="num pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-[var(--mu)]">{curSymbol(conv ? from : home)}</span>
          <input className="mm-input num !py-3.5 !pl-16 text-2xl font-bold" inputMode="decimal" type="number" step="0.01" min="0" placeholder={conv ? `Amount in ${from}` : "0.00"} value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="add-expense-amount-input" />
        </div>
        <button type="button" onClick={() => setConv(!conv)} className={`mm-chip !rounded-[var(--ri)] !px-3 font-bold ${conv ? "on" : ""}`} data-testid="add-expense-convert-toggle" title={t("Convert from another currency")}>
          <ArrowLeftRight className="h-4 w-4" /> <span className="num text-xs">{from}→{home}</span>
        </button>
      </div>
      {conv && <Converter fx={fx} from={from} to={to} setFrom={setFrom} setTo={setTo} home={home} amt={parseFloat(amt) || 0} />}

      <div className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wider text-[var(--mu)]">{t("Choose a category")}</div>
      <CatGrid cats={cats} sel={sel} setSel={setSel} />

      <div className="mt-4 grid gap-2 sm:grid-cols-[170px_1fr]">
        <input className="mm-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="add-expense-date-input" />
        <input className="mm-input" placeholder={t("Note (optional)")} maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} data-testid="add-expense-note-input" />
      </div>
      {err && <p className="mt-3 text-sm text-[var(--dg)]" data-testid="add-expense-error">{err}</p>}
      <button className="mm-btn mt-4 w-full justify-center py-3 text-base" disabled={saving} data-testid="add-expense-submit-button"><Plus className="h-5 w-5" /> {t("Add spending")}</button>
      </form>
    </section>
  );
}
